import { Injectable, signal } from '@angular/core';

/**
 * Describes a single toast in flight. Mirrors the data the V4 card needs.
 * `type` is the AlertType string value ('success' | 'error' | 'warning' | 'info'),
 * kept as a plain string to avoid a circular import with AlertService.
 */
export interface ToastDescriptor {
  id: number;
  message: string;
  type: string;
  duration: number;
  subtitle?: string;
}

/**
 * Single-slot, signal-backed store for the "persiana" toast host. Matches the
 * one-at-a-time behaviour of the old MatSnackBar surface: a new push replaces
 * the current toast (and its auto-dismiss timer).
 *
 * The host renders the card from `current()`; `show()` drives the slide:
 *  - FALSE while the card sits hidden above the header (translateY -110%);
 *  - set TRUE one frame AFTER the card mounts, so the browser registers the
 *    initial transform and ANIMATES the slide-down (a same-frame .show snaps in);
 *  - set back to FALSE to retract (slide up) before the slot is cleared.
 */
@Injectable({ providedIn: 'root' })
export class ToastHostService {
  private readonly _current = signal<ToastDescriptor | null>(null);
  readonly current = this._current.asReadonly();

  private readonly _show = signal(false);
  readonly show = this._show.asReadonly();

  private counter = 0;
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private enterRaf = 0;

  /**
   * Show a toast. Single-slot: replaces any current toast and resets its timer.
   */
  push(d: Omit<ToastDescriptor, 'id'>): void {
    this.clearTimers();

    const id = ++this.counter;
    // Render the card in its hidden (above-the-header) state first...
    this._show.set(false);
    this._current.set({ ...d, id });

    // ...then slide it down on a LATER frame so the slide-down actually animates.
    // A double rAF clears the mount paint, otherwise the browser collapses the
    // -110% -> 0 change into a single frame and the card just snaps in.
    this.enterRaf = requestAnimationFrame(() => {
      this.enterRaf = requestAnimationFrame(() => {
        if (this._current()?.id === id) {
          this._show.set(true);
        }
      });
    });

    this.autoDismissTimer = setTimeout(() => this.dismiss(id), d.duration);
  }

  /**
   * Retract and clear the toast with the given id. Idempotent / stale-guarded:
   * a dismiss for a toast that is no longer current is ignored.
   */
  dismiss(id: number): void {
    if (this._current()?.id !== id) {
      return;
    }

    if (this.autoDismissTimer !== null) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    if (this.enterRaf) {
      cancelAnimationFrame(this.enterRaf);
      this.enterRaf = 0;
    }

    // Retract (host removes .show -> card slides back up behind the header).
    this._show.set(false);

    this.clearTimer = setTimeout(() => {
      // Guard against a newer toast having replaced this one mid-retract.
      if (this._current()?.id === id) {
        this._current.set(null);
      }
      this.clearTimer = null;
    }, 460);
  }

  private clearTimers(): void {
    if (this.autoDismissTimer !== null) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    if (this.enterRaf) {
      cancelAnimationFrame(this.enterRaf);
      this.enterRaf = 0;
    }
  }
}
